// Public Knowledge Base browser — readers can search & filter self-help articles.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, Search, ThumbsUp, ThumbsDown, ArrowLeft, ChevronRight, HelpCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";

const CATEGORY_LABELS = {
    billing: "Billing & Refunds",
    fulfillment: "Orders & Delivery",
    service: "Doctor & Lab",
    safety: "Safety & Adverse Events",
    privacy: "Privacy & Account",
    technical: "Technical Issues",
    other: "Other",
};

const CATEGORY_TONE = {
    billing: "border-amber-200 bg-amber-50 text-amber-800",
    fulfillment: "border-sky-200 bg-sky-50 text-sky-800",
    service: "border-violet-200 bg-violet-50 text-violet-800",
    safety: "border-rose-200 bg-rose-50 text-rose-800",
    privacy: "border-slate-200 bg-slate-50 text-slate-700",
    technical: "border-emerald-200 bg-emerald-50 text-emerald-800",
    other: "border-zinc-200 bg-zinc-50 text-zinc-700",
};

const HelpKB = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const slug = params.get("slug");
    const initialQ = params.get("q") || "";
    const initialCat = params.get("category") || "";

    const [q, setQ] = useState(initialQ);
    const [category, setCategory] = useState(initialCat);
    const [data, setData] = useState({ items: [], by_category: {}, total: 0 });
    const [loading, setLoading] = useState(true);
    const [article, setArticle] = useState(null);
    const [articleLoading, setArticleLoading] = useState(false);
    const [voted, setVoted] = useState(null);

    useEffect(() => {
        let m = true;
        (async () => {
            setLoading(true);
            try {
                const out = await api.get("/kb-articles", { params: { q: q || undefined, category: category || undefined, page_size: 100 } });
                if (m) setData(out.data);
            } finally {
                if (m) setLoading(false);
            }
        })();
        return () => { m = false; };
    }, [q, category]);

    useEffect(() => {
        let m = true;
        (async () => {
            if (!slug) { setArticle(null); setVoted(null); return; }
            setArticleLoading(true);
            try {
                const out = await api.get(`/kb-articles/${slug}`);
                if (m) { setArticle(out.data); setVoted(null); }
            } catch {
                if (m) { setArticle(null); toast.error("Article not found"); }
            } finally {
                if (m) setArticleLoading(false);
            }
        })();
        return () => { m = false; };
    }, [slug]);

    const vote = async (helpful) => {
        if (!article || voted) return;
        try {
            await api.post(`/kb-articles/${article.slug}/vote`, null, { params: { helpful } });
            setVoted(helpful ? "up" : "down");
            toast.success("Thanks for the feedback!");
        } catch {
            toast.error("Could not submit feedback");
        }
    };

    const updateParam = (k, v) => {
        const next = new URLSearchParams(params);
        if (v) next.set(k, v); else next.delete(k);
        setParams(next, { replace: true });
    };

    const categories = useMemo(() => Object.keys(data.by_category || {}), [data]);

    if (slug && article) {
        return (
            <Layout>
                <section className="mx-auto max-w-3xl px-6 py-10 lg:px-12" data-testid="kb-article-detail">
                    <button
                        onClick={() => { updateParam("slug", ""); }}
                        className="mb-4 inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700"
                        data-testid="kb-back-btn"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to help center
                    </button>
                    <div className="rounded-3xl border border-zinc-100 bg-white p-8 shadow-soft">
                        <div className="mb-3 flex items-center gap-2">
                            <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${CATEGORY_TONE[article.category] || CATEGORY_TONE.other}`}>
                                {CATEGORY_LABELS[article.category] || article.category}
                            </span>
                        </div>
                        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 sm:text-3xl" data-testid="kb-article-title">
                            {article.title}
                        </h1>
                        <div className="prose prose-zinc mt-6 max-w-none prose-headings:mt-6" data-testid="kb-article-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body}</ReactMarkdown>
                        </div>
                        <div className="mt-8 rounded-2xl border border-zinc-100 bg-zinc-50/50 p-5 text-center">
                            <p className="text-sm text-zinc-700">Was this helpful?</p>
                            <div className="mt-3 flex items-center justify-center gap-3">
                                <button
                                    onClick={() => vote(true)}
                                    disabled={!!voted}
                                    className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                                        voted === "up"
                                            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                            : "border-zinc-200 bg-white text-zinc-700 hover:border-emerald-200 hover:text-emerald-700"
                                    }`}
                                    data-testid="kb-vote-helpful"
                                >
                                    <ThumbsUp className="h-3.5 w-3.5" /> Yes ({article.helpful_count || 0})
                                </button>
                                <button
                                    onClick={() => vote(false)}
                                    disabled={!!voted}
                                    className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                                        voted === "down"
                                            ? "border-rose-300 bg-rose-50 text-rose-700"
                                            : "border-zinc-200 bg-white text-zinc-700 hover:border-rose-200 hover:text-rose-700"
                                    }`}
                                    data-testid="kb-vote-not-helpful"
                                >
                                    <ThumbsDown className="h-3.5 w-3.5" /> No ({article.not_helpful_count || 0})
                                </button>
                            </div>
                            <p className="mt-4 text-xs text-zinc-500">
                                Still need help?{" "}
                                <button
                                    onClick={() => navigate("/complaints/new")}
                                    className="font-semibold text-brand-700 hover:underline"
                                    data-testid="kb-still-need-help"
                                >
                                    Raise a complaint →
                                </button>
                            </p>
                        </div>
                    </div>
                </section>
            </Layout>
        );
    }

    return (
        <Layout>
            <section className="mx-auto max-w-6xl px-6 py-10 lg:px-12" data-testid="kb-help-center">
                <header className="mb-8">
                    <p className="label-uppercase">Help Center</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
                        Find answers, fast
                    </h1>
                    <p className="mt-1 text-sm text-zinc-500">Browse self-help articles or ask our AI assistant.</p>
                </header>

                {/* Quick actions */}
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Link
                        to="/complaints/new"
                        className="group flex items-start gap-3 rounded-2xl border border-zinc-100 bg-gradient-to-br from-brand-50 to-violet-50 p-5 transition hover:border-brand-300 hover:shadow-sm"
                        data-testid="kb-quick-complaint"
                    >
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-700 to-brand-400 text-white">
                            <Sparkles className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="font-semibold text-zinc-900">Have a complaint?</p>
                            <p className="mt-0.5 text-sm text-zinc-600">Our AI will triage it and suggest a quick fix.</p>
                        </div>
                        <ChevronRight className="h-5 w-5 shrink-0 text-zinc-400 transition group-hover:translate-x-1" />
                    </Link>
                    <Link
                        to="/support"
                        className="group flex items-start gap-3 rounded-2xl border border-zinc-100 bg-white p-5 transition hover:border-brand-200 hover:shadow-sm"
                        data-testid="kb-quick-support"
                    >
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-zinc-100">
                            <HelpCircle className="h-5 w-5 text-brand-700" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="font-semibold text-zinc-900">Open a support ticket</p>
                            <p className="mt-0.5 text-sm text-zinc-600">For order, prescription, or general queries.</p>
                        </div>
                        <ChevronRight className="h-5 w-5 shrink-0 text-zinc-400 transition group-hover:translate-x-1" />
                    </Link>
                </div>

                {/* Search */}
                <div className="mb-5 flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white px-4 py-3 shadow-sm">
                    <Search className="h-4 w-4 shrink-0 text-zinc-400" />
                    <input
                        value={q}
                        onChange={(e) => { setQ(e.target.value); updateParam("q", e.target.value); }}
                        placeholder="Search topics… (e.g., refund, prescription, tracking)"
                        className="flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-400"
                        data-testid="kb-search-input"
                    />
                </div>

                {/* Category chips */}
                <div className="mb-6 flex flex-wrap gap-2">
                    <button
                        onClick={() => { setCategory(""); updateParam("category", ""); }}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition ${!category ? "border-brand-600 bg-brand-600 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-brand-300"}`}
                        data-testid="kb-cat-all"
                    >
                        All
                    </button>
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => { setCategory(cat); updateParam("category", cat); }}
                            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${category === cat ? "border-brand-600 bg-brand-600 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-brand-300"}`}
                            data-testid={`kb-cat-${cat}`}
                        >
                            {CATEGORY_LABELS[cat] || cat} ({data.by_category[cat]?.length || 0})
                        </button>
                    ))}
                </div>

                {/* Article grid */}
                {loading ? (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 skeleton rounded-2xl" />)}
                    </div>
                ) : data.items.length === 0 ? (
                    <EmptyState
                        icon={<BookOpen className="h-10 w-10 text-brand-600" />}
                        title="No articles match your filters"
                        description="Try a different search term or clear the filters."
                    />
                ) : (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="kb-article-list">
                        {data.items.map((a) => (
                            <button
                                key={a.id || a.slug}
                                onClick={() => updateParam("slug", a.slug)}
                                className="group flex items-start gap-3 rounded-2xl border border-zinc-100 bg-white p-5 text-left transition hover:border-brand-200 hover:shadow-sm"
                                data-testid={`kb-article-${a.slug}`}
                            >
                                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-700">
                                    <BookOpen className="h-4 w-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="mb-1 flex items-center gap-2">
                                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${CATEGORY_TONE[a.category] || CATEGORY_TONE.other}`}>
                                            {CATEGORY_LABELS[a.category] || a.category}
                                        </span>
                                    </div>
                                    <p className="font-semibold text-zinc-900 line-clamp-1">{a.title}</p>
                                    <p className="mt-1 text-[11px] text-zinc-400">{a.helpful_count || 0} found this helpful</p>
                                </div>
                                <ChevronRight className="h-5 w-5 shrink-0 text-zinc-400 transition group-hover:translate-x-1" />
                            </button>
                        ))}
                    </div>
                )}

                {articleLoading && <p className="mt-4 text-sm text-zinc-500">Loading article…</p>}
            </section>
        </Layout>
    );
};

export default HelpKB;
